import React, { useState, useEffect, useRef, useCallback } from 'react';

// --- Constants ---
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 600;
const PARTICLE_RADIUS = 4; // Increased particle size
const NUM_PARTICLES = 300; // Number of particles moving at any time
const STICK_DISTANCE = PARTICLE_RADIUS * 3; // Stick distance adjusts automatically
const MAX_TREE_SIZE = 5000; // Stop simulation after this many particles stick
const STEPS_PER_FRAME = 100; // Increased speed 100x
const NUM_INITIAL_SEEDS = 15; // Set number of seeds
const MAX_TOTAL_NODES_FACTOR = 1500; // Stop condition factor

const CoralFractal = () => {
  // --- State and Refs ---
  const canvasRef = useRef(null);
  const [treesState, setTreesState] = useState([]);
  const treesRef = useRef(treesState);
  const [particlesState, setParticlesState] = useState([]);
  const particlesRef = useRef(particlesState);
  const [isRunning, setIsRunning] = useState(true);
  const animationFrameId = useRef(null);
  const simulationTime = useRef(0);

  // --- Utility Functions (Defined FIRST) ---
  const getParticleColor = useCallback((baseHue, time) => {
      // Use baseHue and add a slight time-based variation
      const hue = (baseHue + (time / 20) % 60) % 360; // Slower color shift
      const saturation = 70 + Math.random() * 20;
      const lightness = 50 + Math.random() * 15;
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }, []);

  const createParticle = useCallback(() => {
    const radius = Math.random() * CANVAS_WIDTH * 0.4 + CANVAS_WIDTH * 0.1;
    const angle = Math.random() * Math.PI * 2;
    const startX = CANVAS_WIDTH / 2 + radius * Math.cos(angle);
    // Generate particles across the full height for more even distribution
    const startY = Math.random() * CANVAS_HEIGHT;
    return {
        x: Math.max(PARTICLE_RADIUS, Math.min(CANVAS_WIDTH - PARTICLE_RADIUS, startX)),
        y: Math.max(PARTICLE_RADIUS, Math.min(CANVAS_HEIGHT - PARTICLE_RADIUS, startY))
    };
  }, []);

  // --- Drawing Function (Defined before use) ---
  const draw = useCallback((ctx, currentTree) => {
    // if (simulationTime.current % 100 === 1) { // Keep this commented or adjust frequency
      console.log(`Drawing frame. Tree size: ${currentTree.reduce((sum, tree) => sum + tree.nodes.length, 0)}`);
    // }
    ctx.fillStyle = '#000010';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw all nodes from all trees
    currentTree.forEach(tree => {
        tree.nodes.forEach(p => {
            ctx.beginPath();
            ctx.arc(p.x, p.y, PARTICLE_RADIUS, 0, Math.PI * 2);
            ctx.fillStyle = p.color;
            ctx.fill();
        });
    });
  }, []);

  // --- State Setter Wrappers ---
  const setTrees = useCallback((updater) => {
      setTreesState(currentState => {
          const newState = typeof updater === 'function' ? updater(currentState) : updater;
          treesRef.current = newState;
          return newState;
      });
  }, []);

  const setParticles = useCallback((updater) => {
      setParticlesState(currentState => {
          const newState = typeof updater === 'function' ? updater(currentState) : updater;
          particlesRef.current = newState;
          return newState;
      });
  }, []);

  // --- Simulation Logic Functions (Defined using utilities above) ---
  const resetParticles = useCallback(() => {
    const newParticles = [];
    for (let i = 0; i < NUM_PARTICLES; i++) {
      newParticles.push(createParticle());
    }
    setParticles(newParticles);
  }, [setParticles, createParticle]);

  const startSimulation = useCallback(() => {
     if (animationFrameId.current) return;
     // console.log("Starting simulation loop...");

     const canvas = canvasRef.current;
     if (!canvas) return;
     const ctx = canvas.getContext('2d');
     let frameCount = 0;

     const runFrame = () => {
       frameCount++;
       let currentFrameParticles = particlesRef.current;
       const currentFrameTrees = treesRef.current;
       let frameTotalStuckBatch = []; // { particle: Particle, treeId: number }[]

       for (let step = 0; step < STEPS_PER_FRAME; step++) {
            simulationTime.current += 1;
            const nextStepParticles = [];
            const currentStepStuckBatch = [];

            currentFrameParticles.forEach(p => {
                p.x += Math.random() * 6 - 3;
                p.y += Math.random() * 6 - 3;

                if (p.x < PARTICLE_RADIUS || p.x > CANVAS_WIDTH - PARTICLE_RADIUS ||
                    p.y < PARTICLE_RADIUS || p.y > CANVAS_HEIGHT - PARTICLE_RADIUS) {
                    nextStepParticles.push(createParticle());
                    return;
                }

                let stuck = false;
                let stuckTreeId = null;
                const STICK_DIST_SQ = STICK_DISTANCE * STICK_DISTANCE;

                for (const tree of currentFrameTrees) {
                    for (const treeNode of tree.nodes) {
                        const dx = p.x - treeNode.x;
                        const dy = p.y - treeNode.y;
                        const distSq = dx * dx + dy * dy;

                        if (distSq < STICK_DIST_SQ) {
                            stuck = true;
                            stuckTreeId = tree.id;
                            break;
                        }
                    }
                    if (stuck) break;
                }

                if (stuck && stuckTreeId !== null) {
                    const hitTree = currentFrameTrees.find(t => t.id === stuckTreeId);
                    if (hitTree) {
                        // Assign color using the tree's baseHue and current time
                        const newStuckParticle = { ...p, color: getParticleColor(hitTree.baseHue, simulationTime.current) };
                        currentStepStuckBatch.push({ particle: newStuckParticle, treeId: stuckTreeId });
                    }
                    nextStepParticles.push(createParticle());
                } else {
                    nextStepParticles.push(p);
                }
            });
            currentFrameParticles = nextStepParticles;
            frameTotalStuckBatch = frameTotalStuckBatch.concat(currentStepStuckBatch);
       }

       // --- Log & Update State ---
       if (frameCount % 60 === 0) {
           const totalNodes = treesRef.current.reduce((sum, tree) => sum + tree.nodes.length, 0);
           // console.log(`Frame: ${frameCount}, Total Nodes: ${totalNodes}`);
       }

       let finalTreesState = treesRef.current;
       if (frameTotalStuckBatch.length > 0) {
            setTrees(prevTrees => {
                // Create a deep enough copy to modify nodes array
                const newTrees = prevTrees.map(tree => ({
                     ...tree,
                     nodes: [...tree.nodes] // Copy nodes array
                }));
                frameTotalStuckBatch.forEach(stuckInfo => {
                    const treeIndex = newTrees.findIndex(t => t.id === stuckInfo.treeId);
                    if (treeIndex !== -1) {
                        newTrees[treeIndex].nodes.push(stuckInfo.particle);
                    }
                });
                finalTreesState = newTrees;
                return newTrees;
            });
       }
       setParticles(currentFrameParticles);

       // --- Draw & Check Stop Condition ---
       draw(ctx, finalTreesState);

       const totalFinalNodes = finalTreesState.reduce((sum, tree) => sum + tree.nodes.length, 0);
       const maxTotalNodes = NUM_INITIAL_SEEDS * MAX_TOTAL_NODES_FACTOR;

       if (totalFinalNodes >= maxTotalNodes) {
         console.log("Max total node size reached. Stopping simulation.");
         setIsRunning(false);
         if (animationFrameId.current) {
            cancelAnimationFrame(animationFrameId.current);
            animationFrameId.current = null;
         }
         return;
       }

       // --- Request Next Frame ---
       if (isRunning) {
         animationFrameId.current = requestAnimationFrame(runFrame);
       } else {
           if (animationFrameId.current) {
               cancelAnimationFrame(animationFrameId.current);
               animationFrameId.current = null;
           }
       }
     };

     frameCount = 0;
     animationFrameId.current = requestAnimationFrame(runFrame);
  }, [isRunning, setTrees, setParticles, draw, createParticle, getParticleColor]);

  // --- Simulation Control Effect (Handles Start/Stop) ---
  useEffect(() => {
    if (isRunning) {
      startSimulation(); // Start or resume simulation
    } else {
      // Stop simulation
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    }

    // Cleanup function to stop simulation if component unmounts while running
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    };
  }, [isRunning, startSimulation]); // Depend on isRunning and startSimulation

  // --- Button Handlers ---
  const handleToggleRun = () => {
    setIsRunning(prev => !prev); // Just toggle the state
  };

  const handleReset = useCallback(() => {
     setIsRunning(false); // Stop first
     // Cancel frame handled by control effect
     simulationTime.current = 0;

    // Recreate initial trees (NUM_INITIAL_SEEDS)
    const initialTreesData = [];
    for (let i = 0; i < NUM_INITIAL_SEEDS; i++) {
        initialTreesData.push({
            id: i + 1,
            seedPos: {
                x: CANVAS_WIDTH * (i + 1) / (NUM_INITIAL_SEEDS + 1),
                y: CANVAS_HEIGHT - PARTICLE_RADIUS * 2
            },
            baseHue: (i * (360 / NUM_INITIAL_SEEDS)) % 360
        });
    }

    const initialTrees = initialTreesData.map(data => {
        // Use baseHue for initial seed color
        const seedParticle = { ...data.seedPos, color: getParticleColor(data.baseHue, 0) };
        return {
            id: data.id,
            seed: seedParticle,
            nodes: [seedParticle],
            baseHue: data.baseHue // Store baseHue in the tree object
        };
    });
    setTrees(initialTrees);

     resetParticles();

     const canvas = canvasRef.current;
     if (canvas) {
         const ctx = canvas.getContext('2d');
         draw(ctx, initialTrees);
     }
  }, [resetParticles, setTrees, draw, getParticleColor]);

  // --- Initialization Effect --- // Modified to remove startSimulation call
  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;

    // Define initial trees (NUM_INITIAL_SEEDS)
    const initialTreesData = [];
    for (let i = 0; i < NUM_INITIAL_SEEDS; i++) {
        initialTreesData.push({
            id: i + 1,
            seedPos: {
                x: CANVAS_WIDTH * (i + 1) / (NUM_INITIAL_SEEDS + 1),
                y: CANVAS_HEIGHT - PARTICLE_RADIUS * 2
            },
            baseHue: (i * (360 / NUM_INITIAL_SEEDS)) % 360
        });
    }

    const initialTrees = initialTreesData.map(data => {
        // Use baseHue for initial seed color
        const seedParticle = { ...data.seedPos, color: getParticleColor(data.baseHue, 0) };
        return {
            id: data.id,
            seed: seedParticle,
            nodes: [seedParticle],
            baseHue: data.baseHue // Store baseHue in the tree object
        };
    });

    setTrees(initialTrees);
    resetParticles();

    // No direct startSimulation call here. The control effect handles it based on initial isRunning state.
    // No explicit draw call needed if starting in 'running' state, as runFrame will draw.
    // If starting paused, draw the initial state once.
    if (!isRunning && canvas) {
        const ctx = canvas.getContext('2d');
        // Need the initialTrees directly here as treesRef might not be updated yet
        draw(ctx, initialTrees);
    }

    // Cleanup is handled by the control effect
  }, [resetParticles, setTrees, draw, getParticleColor]); // Removed isRunning and startSimulation from dependencies

  // --- JSX ---
  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>Coral Fractal Simulation</h2>
      <div style={canvasContainerStyle}>
        <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={canvasStyle} />
      </div>
      <div style={controlsStyle}>
        <button onClick={handleToggleRun} style={isRunning ? pauseButtonStyle : playButtonStyle}>
          {isRunning ? '일시정지' : '재생'}
        </button>
        <button onClick={handleReset} style={resetButtonStyle}>
          리셋
        </button>
      </div>
      <div style={infoStyle}>
        <span>총 파티클 수: <strong>{treesState.reduce((sum, tree) => sum + tree.nodes.length, 0)}</strong></span>
      </div>
    </div>
  );
};

// Modern styled components
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #121212 0%, #1a1a2e 100%)',
  color: '#e0e0e0',
  fontFamily: 'Arial, sans-serif'
};

const titleStyle = {
  margin: '0 0 1.5rem 0',
  color: '#ffffff',
  fontSize: '2rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  letterSpacing: '1px'
};

const canvasContainerStyle = {
  padding: '0.5rem',
  background: 'rgba(255,255,255,0.05)',
  borderRadius: '12px',
  boxShadow: '0 10px 20px rgba(0,0,0,0.3), 0 6px 6px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1) inset',
  overflow: 'hidden'
};

const canvasStyle = {
  display: 'block',
  borderRadius: '8px',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.05) inset'
};

const controlsStyle = {
  display: 'flex',
  gap: '1rem',
  marginTop: '1.5rem'
};

const buttonBaseStyle = {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  border: 'none',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.2)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  outline: 'none'
};

const playButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
  color: 'white',
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 7px 14px rgba(0,0,0,0.12), 0 3px 6px rgba(0,0,0,0.08)'
  }
};

const pauseButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #FFC107 0%, #FFA000 100%)',
  color: 'white',
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 7px 14px rgba(0,0,0,0.12), 0 3px 6px rgba(0,0,0,0.08)'
  }
};

const resetButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)',
  color: 'white',
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 7px 14px rgba(0,0,0,0.12), 0 3px 6px rgba(0,0,0,0.08)'
  }
};

const infoStyle = {
  marginTop: '1.5rem',
  padding: '0.75rem 1.25rem',
  background: 'rgba(255,255,255,0.08)',
  borderRadius: '8px',
  fontSize: '0.95rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  letterSpacing: '0.5px'
};

export default CoralFractal; 